import { html, useEffect, useRef, useState }
  from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';

/**
 * A straight knockout, on the wall.
 *
 * The whole bracket is on screen from the moment it is drawn, because that is
 * what people gather round: everybody wants to see who they would meet in the
 * final before a ball is struck. Ties that can be played now are the ones with
 * two names in them; tapping a name puts them through.
 *
 * More than one tie is playable at once in the early rounds. A pub with one
 * table plays them in whatever order it likes and the bracket does not care,
 * so nothing here forces an order.
 */
export function KnockoutScreen({ knockoutId, me, kiosk, onDone }) {
  const [game, setGame] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const sending = useRef(0);
  const era = useRef(0);

  const at = `/api/knockout/${encodeURIComponent(knockoutId)}`;

  useEffect(() => {
    let live = true;
    get(at)
      .then((g) => { if (live) setGame(g); })
      .catch((e) => { if (live) setError(e.detail || e.message); });
    // Somebody scoring a tie on a phone should show here without anybody
    // poking the wall. Guarded by refs, not state: the effect closes over its
    // variables once, so a poll landing mid-tap would put the old bracket back.
    const tick = setInterval(() => {
      if (sending.current) return;
      const mine = era.current;
      get(at).then((g) => {
        if (live && !sending.current && mine === era.current) setGame(g);
      }).catch(() => {});
    }, 8000);
    return () => { live = false; clearInterval(tick); };
  }, [at]);

  const send = async (where, body) => {
    sending.current += 1;
    era.current += 1;
    setBusy(true);
    setError('');
    try {
      setGame(await post(`${at}/${where}`, body || {}));
    } catch (e) {
      setError(e.detail || e.message);
    } finally {
      sending.current -= 1;
      setBusy(false);
    }
  };

  if (error && !game) {
    return html`
      <div class="screen">
        <button class="secondary" onClick=${onDone}>Back</button>
        <p class="error" data-testid="knockout-error">${error}</p>
      </div>`;
  }
  if (!game) return html`<p class="sub">Loading…</p>`;

  const rounds = game.rounds || [];
  const ready = new Set((game.playing || []).map((t) => `${t.round}.${t.tie}`));

  return html`
    <div class="screen koscreen"
         data-testid=${kiosk ? 'knockout-wall' : 'knockout-screen'}>
      <div class="kohead">
        <h2 data-testid="knockout-title">Knockout</h2>
        <button class="secondary" style="width:auto"
                data-testid="knockout-back" onClick=${onDone}>Back</button>
      </div>

      ${game.champion
        ? html`
          <div class="kodone" data-testid="knockout-champion">
            <div class="name">Winner</div>
            <div class="figure">${game.champion.name}</div>
          </div>`
        : game.abandoned
          ? html`<p class="empty" data-testid="knockout-abandoned">
                   This knockout was packed up.
                 </p>`
          : html`
            <p class="sub" data-testid="knockout-left">
              ${`${(game.still_in || []).length} still in, `}
              ${`${game.entrants} started, best of a bracket of ${game.size}`}
            </p>`}

      <div class="kobracket" data-testid="knockout-bracket">
        ${rounds.map((rnd, i) => html`
          <div class="koround" key=${i} data-testid=${`knockout-round-${i + 1}`}>
            <div class="name">${rnd[0] ? rnd[0].name : ''}</div>
            ${rnd.map((tie) => html`
              <${Tie} key=${`${tie.round}.${tie.tie}`} tie=${tie}
                      playable=${ready.has(`${tie.round}.${tie.tie}`)}
                      busy=${busy}
                      onPick=${(who) => send('tie', {
                        round: tie.round, tie: tie.tie, winner: who,
                        event_id: crypto.randomUUID(),
                      })} />`)}
          </div>`)}
      </div>

      <div class="row">
        <button class="secondary" data-testid="knockout-undo"
                disabled=${busy || !game.results}
                onClick=${() => send('undo')}>Undo</button>
        ${!game.over && html`
          <button class="secondary" data-testid="knockout-abandon"
                  disabled=${busy}
                  onClick=${() => send('abandon')}>Pack up</button>`}
      </div>
      ${error && html`<p class="error" data-testid="knockout-tie-error">
                        ${error}
                      </p>`}
    </div>`;
}

/**
 * One tie: two slots, and a tap on whoever won.
 *
 * A bye is drawn as the person and the word, not as a blank — somebody
 * looking for their own name needs to find it, and "you are through without
 * playing" is a thing to be told rather than to work out from an empty box.
 */
function Tie({ tie, playable, busy, onPick }) {
  const slot = (who, other) => {
    if (!who) {
      return html`<div class="koslot empty">—</div>`;
    }
    const won = tie.winner && tie.winner.user_id === who.user_id;
    const lost = tie.winner && !won;
    if (!playable) {
      return html`
        <div class=${`koslot ${won ? 'won' : ''} ${lost ? 'lost' : ''}`}
             data-testid=${`knockout-slot-${who.user_id}`}>
          ${who.name}
        </div>`;
    }
    return html`
      <button class="koslot pick" disabled=${busy}
              data-testid=${`knockout-pick-${tie.round}-${tie.tie}-${who.user_id}`}
              onClick=${() => onPick(who.user_id)}>${who.name}</button>`;
  };

  return html`
    <div class=${`kotie ${playable ? 'ready' : ''}`}
         data-testid=${`knockout-tie-${tie.round}-${tie.tie}`}>
      ${slot(tie.a, tie.b)}
      ${slot(tie.b, tie.a)}
      ${tie.bye && (tie.a || tie.b) && html`
        <div class="kobye" data-testid="knockout-bye">Bye</div>`}
    </div>`;
}
